import React, { useMemo } from 'react'
import PropTypes from 'prop-types'

import Table from '../../../../../../components/Table'
import StartDateHeader from './Cell/StartDateHeader'

import {
  ExpandableRowsOpenStatesContext,
  ExpandableRowsOpenStatesProvider
} from '../../../../../LandingPage/ControllerLandingPageContent/ManagementReporting/InstallationReport/InstallationReportTable/ExpandableRowsOpenStatesContext'

import usePermissions from '../../../../../../hooks/usePermissions'
import { useUploadedFilesColumns } from './Cell/useUploadedFilesColumns'
import { useRequestedFilesColumns } from './Cell/useRequestedFilesColumns'
import { useGetUpdatingId, useGetUpdatingIds, useOpenFileForm } from './hooks'

import { formatDateFullYear } from '../../../../../../constants/dates'
import { tableColumnsSize } from '../const/columns'
import { MEDIA_ORDER_CUSTOMISE_DATES_PERMISSION } from '../../../../../../constants/permissions'

import useStyles from './styles'

const RequestedFilesTableData = ({ files, bookedMediaId }) => {
  const classes = useStyles()
  const getItemUpdatingId = useGetUpdatingId()
  const { rowIds, subRowsIds } = useGetUpdatingIds(files)

  const permissions = usePermissions()
  const canUpdateCustomDates = permissions.can('manage', MEDIA_ORDER_CUSTOMISE_DATES_PERMISSION)

  const openUploadFileForm = useOpenFileForm()

  const periodStartColumn = useMemo(
    () => ({
      header: canUpdateCustomDates ? () => <StartDateHeader bookedMediaId={bookedMediaId} /> : 'Period Start',
      Cell: ({ requested_file }) => formatDateFullYear(requested_file.date_start),
      style: { maxWidth: tableColumnsSize.startDate, fontSize: 12 }
    }),
    [bookedMediaId, canUpdateCustomDates]
  )

  const requestedFilesColumns = useRequestedFilesColumns({ openUploadFileForm, periodStartColumn })
  const uploadedFilesColumns = useUploadedFilesColumns({ openUploadFileForm, periodStartColumn })

  const rowTemplate = useMemo(() => {
    return {
      getRowClassName: data => {
        return data?.uploaded_files?.length === 0 && classes.notUploadedRow
      },
      params: {
        // when there are more than one uploaded file, the row should be expandable
        isExpandable: data => data?.uploaded_files?.length > 1,
        renderExpandedContent: data => {
          const { uploaded_files: uploadedFiles, id: requestedRowId, ...reusableData } = data

          const uploadedFilesData = uploadedFiles.map(uploadedFile => ({
            uploaded_file: uploadedFile,
            ...reusableData,
            requestedRowId,
            id: uploadedFile.id
          }))

          return (
            <Table
              className={classes.uploadedFilesTable}
              rowTemplate={{
                rowClassName: classes.uploadedFilesTableRow
              }}
              data={uploadedFilesData}
              cols={uploadedFilesColumns}
              updatingIds={subRowsIds}
              hideFooterRow
              hideHeadlineRow
            />
          )
        },
        // when there is only one uploaded file, the row should not be expandable
        customColumnsKey: data => (data?.uploaded_files?.length === 1 ? 'uploadedFile' : null)
      }
    }
  }, [
    classes.notUploadedRow,
    classes.uploadedFilesTable,
    classes.uploadedFilesTableRow,
    subRowsIds,
    uploadedFilesColumns
  ])

  return (
    <ExpandableRowsOpenStatesProvider>
      <Table
        hideFooterRow
        data={files}
        rowTemplate={rowTemplate}
        cols={requestedFilesColumns}
        customColumns={{
          uploadedFile: uploadedFilesColumns
        }}
        itemUpdatingId={getItemUpdatingId}
        updatingIds={rowIds}
        expandableRowsOpenStatesContext={ExpandableRowsOpenStatesContext}
      />
    </ExpandableRowsOpenStatesProvider>
  )
}

RequestedFilesTableData.propTypes = {
  files: PropTypes.array.isRequired
}

export default RequestedFilesTableData
